import {Component, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {
  Area,
  AreaResponse,
  DownloadCenterDocument,
  EntityType,
  ExampleDocument,
  ExampleDocumentResponse
} from '../domains/common.domain';
import {DocumentResponse, LookUpData, LookupOptionsData} from '../jurisdiction/jurisdiction.domain';
import {DownloadCenterService} from '../services/download-center.service';
import {ErrorService} from '../services/error/error.service';
import {LocalStorageService} from '../services/local-storage.service';
import {NetzTranslateService} from '../services/netz-translate.service';
import {EnvironmentService} from '../services/environment.service';
import {forkJoin, of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AllDropdownKeys} from '../jurisdiction/jurisdiction.constants';
import {CommonService} from '../services/common.service';
import {FileUpload} from 'primeng';

@Component({
  selector: 'app-new-additions',
  templateUrl: './new-additions.component.html',
  styleUrls: ['./new-additions.component.scss']
})
export class NewAdditionsComponent implements OnInit {

  areasFG: FormGroup;
  documentsFG: FormGroup;
  exampleDocumentsFG: FormGroup;

  documents: any;
  exampleDocuments: ExampleDocument[];
  areas: any;
  isLoading: boolean;
  documentsLoaded: boolean;
  areasLoaded: boolean;

  documentIds: string[];
  areaIds: string[];

  areaItems: FormArray;
  docItems: FormArray;
  exampleDocumentsArray: FormArray;

  showNewDevAreaSection: boolean;
  showSampleDocuments: boolean;
  documentTypes: LookupOptionsData[];
  documentTypeKey: string;

  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private downloadCenterService: DownloadCenterService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private translateService: NetzTranslateService,
    private localStorageService: LocalStorageService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.documentsLoaded = false;
    this.areasLoaded = false;
    this.documentIds = [];
    this.areaIds = [];
    this.documentTypeKey = AllDropdownKeys.DocumentType;
    this.documentTypes = this.commonService.getDropdownData(this.documentTypeKey);
    this.documentTypes.pop();
    this.documentTypes.forEach((document) => {
      document.inactive = true;
    });
    this.areasFG = this.formBuilder.group({
      areaItems: this.formBuilder.array([]),
    });
    this.documentsFG = this.formBuilder.group({
      docItems: this.formBuilder.array([]),
    });
    this.exampleDocumentsFG = this.formBuilder.group({
      docItems: this.formBuilder.array([]),
    });
    this.showSampleDocuments = this.environmentService.sampleDocuments;
    this.showNewDevAreaSection = this.environmentService.showNewDevAreaSection;

    this.areaItems = (this.areasFG.get('areaItems') as FormArray);
    this.docItems = (this.documentsFG.get('docItems') as FormArray);
    this.exampleDocumentsArray = (this.exampleDocumentsFG.get('docItems') as FormArray);

    this.getData();
  }

  addMore(entityType: string): void {
    if (entityType === EntityType.Document) {
      this.docItems.push(this.formBuilder.group({
        documentName: new FormControl(''),
        fileName: new FormControl('')
      }));
    } else if (entityType === EntityType.Area) {
      this.areaItems.push(this.formBuilder.group({
        areaName: new FormControl(''),
        forPower: new FormControl({value: false, disabled: true}),
        forGas: new FormControl({value: false, disabled: true}),
        forWater: new FormControl({value: false, disabled: true}),
        forHeating: new FormControl({value: false, disabled: true})
      }));
    }
  }

  handleMediaSelectors(event: InputEvent, index: number) {
    if (event.data) {
      this.areaItems.controls[index].get('forPower').enable();
      this.areaItems.controls[index].get('forGas').enable();
      this.areaItems.controls[index].get('forWater').enable();
      this.areaItems.controls[index].get('forHeating').enable();
    } else {
      this.areaItems.controls[index].get('forPower').disable();
      this.areaItems.controls[index].get('forGas').disable();
      this.areaItems.controls[index].get('forWater').disable();
      this.areaItems.controls[index].get('forHeating').disable();
    }
  }

  onUploadDocument(event: any, index: number, uploader: FileUpload): void {
    this.isLoading = true;
    const name = (this.docItems.controls[index] as FormGroup).value.documentName;
    this.downloadCenterService.uploadDocument(event, name).subscribe((response: DocumentResponse) => {
      uploader.clear();
      (this.docItems.controls[index] as FormGroup).controls.fileName
        .setValue(event.currentFiles && event.currentFiles[0]
          && event.currentFiles[0].name);
      this.documentIds[index] = response.document[0].document_id;
      (this.documentsFG.get('docItems') as FormArray).controls[index].get('documentName').disable();
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(response, true);
    }, error => {
      this.errorService.throwErrorIfFailure(error, true);
    });
  }

  removeEntity(entityType: string, index: number): void {
    if (entityType === EntityType.Document) {
      if (this.documentIds[index]) {
        this.isLoading = true;
        this.downloadCenterService.removeEntity(entityType, this.documentIds[index]).subscribe((response) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          this.documentIds.splice(index, 1);
          (this.documentsFG.get('docItems') as FormArray).removeAt(index);
        }, error => {
          this.errorService.throwErrorIfFailure(error, true);
        });
      } else {
        (this.documentsFG.get('docItems') as FormArray).removeAt(index);
      }
    } else if (entityType === EntityType.Area) {
      if (this.areaIds[index]) {
        this.isLoading = true;
        this.downloadCenterService.removeEntity(entityType, this.areaIds[index]).subscribe((response) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);
          this.areaIds.splice(index, 1);
          (this.areasFG.get('areaItems') as FormArray).removeAt(index);
          this.updateLookUpData();
        }, error => {
          this.errorService.throwErrorIfFailure(error, true);
        });
      } else {
        (this.areasFG.get('areaItems') as FormArray).removeAt(index);
      }
    }
  }

  downloadDocument(index: number, fileName: string): void {
    this.downloadCenterService.readDocument(this.documentIds[index]).subscribe((response) => {
      this.downloadCenterService.downloadDocument(response, fileName);
      this.errorService.throwErrorIfFailure(response, true);
    }, error => {
      this.errorService.throwErrorIfFailure(error, true);
    });
  }

  saveOrUpdateNewArea(index: number, values: any): void {
    this.isLoading = true;
    const reqObj = {
      name: values.areaName.value,
      power: values.forPower.value ? '1' : '0',
      gas: values.forGas.value ? '1' : '0',
      water: values.forWater.value ? '1' : '0',
      districtheat: values.forHeating.value ? '1' : '0',
    };
    if (this.areaIds[index] !== undefined) {
      this.downloadCenterService.modifyArea(reqObj, this.areaIds[index]).subscribe((response) => {
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
      }, error => {
        this.errorService.throwErrorIfFailure(error, true);
      });
    } else {
      this.downloadCenterService.addNewArea(reqObj).subscribe((response: AreaResponse) => {
        this.areaIds[index] = response.developmentarea[0].area_id;
        (this.areasFG.get('areaItems') as FormArray).controls[index].get('areaName').disable();
        this.updateLookUpData();
        this.isLoading = false;
        this.errorService.throwErrorIfFailure(response, true);
      }, error => {
        this.errorService.throwErrorIfFailure(error, true);
      });
    }
  }

  updateLookUpData(): void {
    const translations = [];
    this.translateService.getLangs().map(lang => {
      this.translate.getTranslation(lang).subscribe((data) => {
        this.downloadCenterService.updateLookUp(lang).subscribe((response) => {
          let translationData: any;
          if (data.length) {
            translationData = data[0];
            translationData.lookup = (response.lookup as LookUpData);
            translations[lang] = translationData.lookup;
          } else {
            translationData = data;
          }
          this.translate.setTranslation(lang, translationData);
          if (lang === this.translateService.getCurrentLang()) {
            this.updateLookupInLocal();
          }
          this.errorService.throwErrorIfFailure(response, true);
        }, error => {
          this.errorService.throwErrorIfFailure(error, true);
        });
      }, error => {
        this.errorService.throwErrorIfFailure(error, true);
      });
    });
  }

  updateLookupInLocal(): void {
    this.translate.getTranslation(this.translateService.getCurrentLang()).subscribe(data => {
      this.localStorageService.setItem('lookup_data', data[1].lookup);
    });
  }

  addExampleDocument(): void {
    this.exampleDocumentsArray.push(this.formBuilder.group({
      documentId: new FormControl(''),
      documentName: new FormControl(''),
      documentType: new FormControl(''),
      fileName: new FormControl(''),
    }));
    this.checkDocumentType();
  }

  downloadExampleDocument(id: string, fileName: string): void {
    this.downloadCenterService.getExampleDocumentById(id).subscribe((response) => {
      this.errorService.throwErrorIfFailure(response, true);
      this.downloadCenterService.downloadDocument(response, fileName);
    }, error => {
      this.errorService.throwErrorIfFailure(error, true);
    });
  }

  onUploadExampleDocument(event: any, item: any, index: number, uploader: FileUpload): void {
    this.isLoading = true;
    const name = item.get('documentName').value;
    this.downloadCenterService.uploadExampleDocument(event, item.get('documentType').value, name)
      .subscribe((response: DocumentResponse) => {
        this.isLoading = false;
        uploader.clear();
        this.errorService.throwErrorIfFailure(response, true);
        (this.exampleDocumentsArray.controls[index] as FormGroup).controls.fileName
          .setValue(event.currentFiles && event.currentFiles[0]
            && event.currentFiles[0].name);
        (this.exampleDocumentsArray.controls[index] as FormGroup).controls.documentId
          .setValue(response.document[0].document_id);
        (this.exampleDocumentsFG.get('docItems') as FormArray).controls[index].get('documentType').disable();
        (this.exampleDocumentsFG.get('docItems') as FormArray).controls[index].get('documentName').disable();
      }, error => {
        this.isLoading = false;
        uploader.clear();
        this.errorService.throwErrorIfFailure(error, true);
      });
  }

  deleteExampleDocument(documentId: string, index: number): void {
    if (documentId) {
      this.isLoading = true;
      this.downloadCenterService.deleteExampleDocument(documentId)
        .subscribe((response) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(response, true);

          this.exampleDocumentsArray.removeAt(index);
        }, (error) => {
          this.isLoading = false;
          this.errorService.throwErrorIfFailure(error, true);
        });
    } else {
      this.exampleDocumentsArray.removeAt(this.exampleDocumentsArray.length - 1);
    }
  }

  checkDocumentType(): void {
    const selectedDocumentTypes: string[] = [];
    this.exampleDocumentsArray.controls.forEach((element: FormGroup) => {
      selectedDocumentTypes.push(element.controls.documentType.value);
    });

    this.documentTypes.forEach((document) => {
      document.inactive = selectedDocumentTypes.indexOf(document.value) > -1;
    });
  }

  eventDisabled(inactive: boolean): void {
    if (inactive) {
      event.stopPropagation();
    }
  }

  private getData(): void {
    const requests = [
      this.downloadCenterService.getAllData('documents').pipe(
        catchError(error => of(error))),
    ];
    if (this.showNewDevAreaSection) {
      requests.push(
        this.downloadCenterService.getAllData('areas').pipe(
          catchError(error => of(error))));
    }
    if (this.showSampleDocuments) {
      requests.push(this.downloadCenterService.getAllExampleDocuments().pipe(
        catchError(error => of(error))));
    }

    forkJoin(requests).subscribe(response => {
      this.isLoading = false;

      this.parseDocuments(response[0]);
      if (this.showNewDevAreaSection) {
        this.parseAreas(response[1]);
      }
      if (this.showSampleDocuments) {
        this.parseExampleDocuments(response[this.showNewDevAreaSection ? 2 : 1]);
      }
    }, error => {
      this.isLoading = false;
      this.errorService.throwErrorIfFailure(error, true);
    });
  }

  private parseDocuments(documents: DownloadCenterDocument) {
    this.errorService.throwErrorIfFailure(documents, true);
    this.documents = documents.document;
    documents.document.map((document, index) => {
      (this.documentsFG.get('docItems') as FormArray).push(this.formBuilder.group({
        documentName: new FormControl({
          value: document.name,
          disabled: true
        }),
        fileName: new FormControl(document.filename)
      }));
      this.documentIds[index] = document.document_id;
    });
  }

  private parseAreas(areas: Area) {
    this.errorService.throwErrorIfFailure(areas, true);
    this.areas = areas?.devarea || [];
    areas.devarea.forEach((area, index) => {
      (this.areasFG.get('areaItems') as FormArray).push(this.formBuilder.group({
        areaName: new FormControl({
          value: area.name,
          disabled: true
        }),
        forPower: new FormControl(area.power === '1'),
        forGas: new FormControl(area.gas === '1'),
        forWater: new FormControl(area.water === '1'),
        forHeating: new FormControl(area.districtheat === '1'),
      }));
      this.areaIds[index] = area.area_id;
    });
  }

  private parseExampleDocuments(exampleDocuments: ExampleDocumentResponse) {
    this.errorService.throwErrorIfFailure(exampleDocuments, true);
    this.exampleDocuments = exampleDocuments?.document || [];
    this.exampleDocuments.forEach((document) => {
      (this.exampleDocumentsFG.get('docItems') as FormArray).push(this.formBuilder.group({
        documentId: new FormControl(document.document_id),
        documentName: new FormControl({
          value: document.name,
          disabled: true
        }),
        documentType: new FormControl({
          value: document.sk_document_type,
          disabled: true
        }),
        fileName: new FormControl(document.filename)
      }));
    });
    this.checkDocumentType();
  }
}
